/**
 * R7.2.2F3 — canonical Customer sellability contract for Add buttons and availability copy.
 */
import type { CatalogV2Product } from "./catalog-v2-types.js";

export type CustomerSellabilityOutcome =
  | "SELLABLE"
  | "OUT_OF_STOCK"
  | "INACTIVE"
  | "NOT_CUSTOMER_VISIBLE"
  | "STALE"
  | "UNKNOWN";

export function resolveCustomerSellability(
  product: CatalogV2Product | null | undefined,
): CustomerSellabilityOutcome {
  if (!product) return "UNKNOWN";
  const id = String(product.id ?? "").trim();
  if (!id) return "UNKNOWN";

  const stock = String(product.stockStatus ?? "").toLowerCase();
  if (stock === "out_of_stock") return "OUT_OF_STOCK";

  if (product.isSellable === false) {
    const reason = String(product.sellabilityReason ?? "").toLowerCase();
    if (reason.includes("stock") || reason.includes("out_of_stock")) return "OUT_OF_STOCK";
    if (reason.includes("inactive") || reason.includes("hidden") || reason.includes("visible")) {
      return "NOT_CUSTOMER_VISIBLE";
    }
    return "INACTIVE";
  }

  const priced =
    product.isPriced === true ||
    (typeof product.priceUah === "number" && Number.isFinite(product.priceUah) && product.priceUah > 0);

  if (!priced) return "NOT_CUSTOMER_VISIBLE";

  // Missing stock_qty in lightweight DTOs must NOT imply zero — stockStatus is authoritative.
  return "SELLABLE";
}

export function sellabilityBlocksAdd(outcome: CustomerSellabilityOutcome): boolean {
  return outcome !== "SELLABLE" && outcome !== "UNKNOWN";
}

export function sellabilityMessageUk(outcome: CustomerSellabilityOutcome): string {
  switch (outcome) {
    case "OUT_OF_STOCK":
      return "Товар зараз відсутній на складі.";
    case "INACTIVE":
    case "NOT_CUSTOMER_VISIBLE":
      return "Цей товар щойно став недоступним.";
    case "STALE":
      return "Дані про товар застаріли. Оновіть сторінку.";
    case "UNKNOWN":
      return "Не вдалося перевірити доступність товару.";
    default:
      return "Товар зараз недоступний.";
  }
}

/** Resolve catalog product by canonical id or Prom external id. */
export function resolveCatalogProductByAnyId(
  id: string,
  catalogById: Map<string, CatalogV2Product>,
  shellById: Map<string, CatalogV2Product>,
): CatalogV2Product | undefined {
  const key = String(id ?? "").trim();
  if (!key) return undefined;
  const direct = catalogById.get(key) ?? shellById.get(key);
  if (direct) return direct;
  for (const p of catalogById.values()) {
    if (p.id === key || p.promProductId === key) return p;
  }
  for (const p of shellById.values()) {
    if (p.id === key || p.promProductId === key) return p;
  }
  return undefined;
}
